import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import type { Icon } from '@slite/icons'
import {
  ChatGPTIcon,
  ClaudeGreyIcon,
  CursorGreyIcon,
  GeminiIcon,
  LovableIcon,
} from '@slite/icons'

type ClientLogo = {
  id: string
  Icon: Icon
  bgColor?: string
  iconColor?: string
  size?: string
}

const LOGO_SETS: ClientLogo[][] = [
  [
    {
      id: 'claude',
      Icon: ClaudeGreyIcon,
      bgColor: '#D97757',
      iconColor: '#FFFFFF',
    },
    { id: 'chatgpt', Icon: ChatGPTIcon },
    {
      id: 'cursor',
      Icon: CursorGreyIcon,
      bgColor: '#28231D',
      iconColor: '#FFFFFF',
    },
  ],
  [
    { id: 'lovable', Icon: LovableIcon, size: '0.55em' },
    { id: 'gemini', Icon: GeminiIcon },
    { id: 'chatgpt', Icon: ChatGPTIcon },
  ],
]

const SWAP_INTERVAL_MS = 5000
const STAGGER_S = 0.2
// em-based so the badges scale with the hero title's responsive font-size
const BADGE_SIZE = '1.1em'
const BADGE_OVERLAP = '-0.31em'
const LOGO_SIZE = '0.625em'

export const AnimatingCompanies = () => {
  const [setIndex, setSetIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(
      () => setSetIndex((index) => (index + 1) % LOGO_SETS.length),
      SWAP_INTERVAL_MS
    )
    return () => clearInterval(interval)
  }, [])

  return (
    <span
      aria-hidden="true"
      className="flexbox-inline"
      style={{ verticalAlign: 'middle', marginTop: '-0.19em' }}
    >
      {LOGO_SETS[setIndex].map(
        ({ id, Icon, bgColor, iconColor, size }, slot) => (
          <span
            key={slot}
            className="position-relative"
            style={{
              width: BADGE_SIZE,
              height: BADGE_SIZE,
              marginLeft: slot === 0 ? 0 : BADGE_OVERLAP,
            }}
          >
            <AnimatePresence initial={false}>
              <motion.span
                key={id}
                initial={{
                  opacity: 0,
                  x: 0,
                  scale: 0.7,
                  filter: 'blur(8px)',
                }}
                animate={{ opacity: 1, x: 0, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, x: 0, scale: 0.7, filter: 'blur(8px)' }}
                transition={{
                  duration: 0.5,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  delay: slot * STAGGER_S,
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  backgroundColor: bgColor || 'var(--full-white)',
                  border: '0.06em solid var(--full-white)',
                  boxShadow:
                    '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 2px 6px 0 rgba(0, 0, 0, 0.05), 0 4px 12px 0 rgba(0, 0, 0, 0.01)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon size={size || LOGO_SIZE} color={iconColor} />
              </motion.span>
            </AnimatePresence>
          </span>
        )
      )}
    </span>
  )
}
